/**
 * PrintingFilesPanel — the 'printing' Console area (see `areas.ts`).
 *
 * The artwork pipeline: print agent, cloud storage (GCS + Tencent COS),
 * colour codes, RIP routing, platen size, platen variants, print file reuse,
 * print size rules,
 * store SKUs for
 * standard products, self-heal, designs source, and the issue/redo reason
 * lists. Renders in the same order as the `areas.ts`
 * 'printing' section list.
 */
import { useTranslation } from 'react-i18next';
import { Printer, Cloud, Palette, Route, Ruler, Frame, Scaling, HeartPulse, Images, FileWarning, RotateCcw, Scissors, Copy } from 'lucide-react';
import { FilmCutColorSection } from '../components/FilmCutColorSection';
import { PrintAgentSection } from '../components/PrintAgentSection';
import { GcsCloudStorageSection } from '../components/GcsCloudStorageSection';
import { ColourCodesSection } from '../components/ColourCodesSection';
import { RipRoutingSection } from '../components/RipRoutingSection';
import { PrintSizeRulesSection } from '../components/PrintSizeRulesSection';
import { PlatenSizeRulesSection } from '../components/PlatenSizeRulesSection';
import { PlatenVariantsSection } from '../components/PlatenVariantsSection';
import { PrintFileReuseSection } from '../components/PrintFileReuseSection';
import { LinkedLine } from '../components/console/SectionLink';
import { DeclaredStoreSkusSection } from '../components/DeclaredStoreSkusSection';
import { PrintFileSelfHealSection } from '../components/PrintFileSelfHealSection';
import { LocationPrintersList } from '../components/LocationPrintersList';
import { DesignsSourceSection } from '../components/DesignsSourceSection';
import { PrintFileIssueReasonsSection } from '../components/PrintFileIssueReasonsSection';
import { RedoReasonsSection } from '../components/RedoReasonsSection';
import { TencentCosSection } from '../sections/TencentCosSection';
import { SettingsSection } from '../components/console/SettingsSection';

export function PrintingFilesPanel() {
  const { t } = useTranslation('settings');
  return (
    <>
      {/* (R2-F2) Title + description kept in lockstep with the same card's
          `areas.ts` entry — the search result and the card header must not
          describe different cards. */}
      <SettingsSection
        id="print-agent"
        title="Print agent"
        description="Desktop bridge for DTG printing and shipping labels, and the printers registered at this location."
        icon={Printer}
        scope="browser"
      >
        <PrintAgentSection />
        {/* BLU-877: what this floor actually prints in, read from the fleet
            itself. This is what replaces the retired "Print file formats"
            card — the question an operator opens Printing Files to answer is
            unchanged, but the honest answer is the printers registered here,
            not a scalar somebody typed. It lives on THIS card because the
            print agent is what carries the declaration. */}
        <LocationPrintersList />
      </SettingsSection>

      <SettingsSection
        id="gcs"
        title="Google Cloud Storage"
        description="Bring-your-own GCS bucket for print files."
        icon={Cloud}
        scope="org"
      >
        <GcsCloudStorageSection />
      </SettingsSection>

      <TencentCosSection />

      <SettingsSection
        id="colour-codes"
        title="Colour codes"
        description="PixelWrangler colour-code alias map used by the bridge."
        icon={Palette}
        scope="org"
      >
        <ColourCodesSection />
      </SettingsSection>

      {/* Title + description come from the `settings` namespace: this card is
          the one on this panel a zh-reading RIP operator has to act on, and the
          keys already existed with no consumer. (R2-F2) The EN wording is kept
          in lockstep with the same card's `areas.ts` entry — the search result
          and the card header must not describe different cards. */}
      <SettingsSection
        id="rip-routing"
        title={t('production.advanced.ripRouting')}
        description={t('production.advanced.ripRoutingDesc')}
        icon={Route}
        scope="org"
      >
        <RipRoutingSection />
      </SettingsSection>

      {/* Title + description come from the `settings` namespace (Thread Lab
          reads this card in zh). (R2-F2) The EN wording is kept in lockstep
          with the same card's `areas.ts` entry. Phase 86 Round 4: Platen
          size, Platen variants and Print size rules read as one system, in
          that order, each pointing at the next by name. */}
      <SettingsSection
        id="platen-size"
        title={t('platenSize.title')}
        description={
          <LinkedLine
            text={(link) => t('platenSize.description', { link })}
            linkText={t('platenSize.descriptionLink')}
            sectionId="platen-variants"
          />
        }
        icon={Frame}
        scope="org"
      >
        <PlatenSizeRulesSection />
      </SettingsSection>

      {/* Phase 86 Round 4: each platen's Large and Small (zh 压板规格). Title
          + description are `settings` keys, kept in lockstep with the same
          card's `areas.ts` entry. */}
      <SettingsSection
        id="platen-variants"
        title={t('platenVariants.title')}
        description={t('platenVariants.description')}
        icon={Scaling}
        scope="org"
      >
        <PlatenVariantsSection />
      </SettingsSection>

      {/* Phase 91 (D-07): the product types that never reuse a design ID's
          print file in the Upload Studio. Title + description are `settings`
          keys, kept in lockstep with the same card's `areas.ts` entry. */}
      <SettingsSection
        id="print-file-reuse"
        title={t('printFileReuse.title')}
        description={t('printFileReuse.description')}
        icon={Copy}
        scope="org"
      >
        <PrintFileReuseSection />
      </SettingsSection>

      {/* Hard-coded English, as before. Kept in lockstep with the same card's
          `areas.ts` entry, word for word. */}
      <SettingsSection
        id="print-size-rules"
        title="Print size rules"
        description="Per-client print dimensions (cm) by product type and size: overrides incoming artwork size. Not used for platen files."
        icon={Ruler}
        scope="org"
      >
        <PrintSizeRulesSection />
      </SettingsSection>

      {/* Renders its own SettingsSection (id `store-skus`) so the switch state
          can sit in the header. Title + description are the same `settings`
          keys as its `areas.ts` entry. */}
      <DeclaredStoreSkusSection />

      <SettingsSection
        id="film-cut-color"
        title="Film trim-line colour"
        description="The colour the cutting machine detects and cuts along on DTF film headers."
        icon={Scissors}
        scope="location"
      >
        <FilmCutColorSection />
      </SettingsSection>

      <SettingsSection
        id="print-file-self-heal"
        title="Print-file self-heal"
        // (R2-F2) Kept in lockstep with the same card's `areas.ts` entry — the
        // search result and the card header must not describe different cards.
        // BLU-877: absorbed the standards auto-heal half of the retired
        // "Print file formats" card's description, because that toggle now
        // lives here and it is what an operator hunting "recover standard
        // products" is actually looking for.
        description="Detect and recover missing print files at batch time, recover missing standard files from the product's artwork links, and choose whether the print station trusts that check."
        icon={HeartPulse}
        scope="location"
      >
        <PrintFileSelfHealSection embedded />
      </SettingsSection>

      <SettingsSection
        id="designs-source"
        title="Designs source"
        description="Google Drive designs folder for hands-off self-heal."
        icon={Images}
        scope="org"
      >
        <DesignsSourceSection embedded />
      </SettingsSection>

      <SettingsSection
        id="print-file-issue-reasons"
        title="Print-file issue reasons"
        description="Reasons available at the printer station when flagging a file."
        icon={FileWarning}
        scope="org"
      >
        <PrintFileIssueReasonsSection embedded />
      </SettingsSection>

      <SettingsSection
        id="redo-reasons"
        title="Redo reasons"
        description="Reasons available at the catcher station when flagging a redo."
        icon={RotateCcw}
        scope="org"
      >
        <RedoReasonsSection embedded />
      </SettingsSection>
    </>
  );
}
